'use client';

import { useState, useEffect } from 'react';
import HeroSection from './HeroSection';
import QuickAccessCards from './QuickAccessCards';
import KnowledgeBaseSection from './KnowledgeBaseSection';
import VideoTutorialsSection from './VideoTutorialsSection';
import LiveChatSection from './LiveChatSection';
import TicketSystemSection from './TicketSystemSection';
import MaintenanceSection from './MaintenanceSection';
import CommunityQASection from './CommunityQASection';
import ContactOptionsSection from './ContactOptionsSection';

interface TicketFormData {
  name: string;
  email: string;
  subject: string;
  category: string;
  priority: string;
  description: string;
}

export default function SupportCenterInteractive() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [isZoomModalOpen, setIsZoomModalOpen] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const quickAccessCards = [
  {
    id: 1,
    title: "Getting Started",
    description: "Setup guides and initial configuration",
    icon: "RocketLaunchIcon",
    link: "/getting-started",
    color: "bg-accent"
  },
  {
    id: 2,
    title: "Troubleshooting",
    description: "Common issues and solutions",
    icon: "WrenchScrewdriverIcon",
    link: "/troubleshooting",
    color: "bg-quantum-purple"
  },
  {
    id: 3,
    title: "Software Updates",
    description: "Latest releases and patch notes",
    icon: "ArrowPathIcon",
    link: "/software-updates",
    color: "bg-energy-teal"
  },
  {
    id: 4,
    title: "User Manuals",
    description: "Comprehensive device documentation",
    icon: "BookOpenIcon",
    link: "/user-manuals",
    color: "bg-cta-red"
  }];


  const knowledgeBaseCategories = [
  {
    id: 1,
    title: "Device Setup & Configuration",
    icon: "Cog6ToothIcon",
    articleCount: 24,
    articles: [
    { id: 101, title: "Initial Device Setup and Calibration", views: 3420 },
    { id: 102, title: "Connecting to Computer Systems", views: 2890 },
    { id: 103, title: "Software Installation Guide", views: 2650 },
    { id: 104, title: "Network Configuration Best Practices", views: 1980 }]

  },
  {
    id: 2,
    title: "Measurement Techniques",
    icon: "ChartBarIcon",
    articleCount: 32,
    articles: [
    { id: 201, title: "Proper Finger Placement for Accurate Readings", views: 4120 },
    { id: 202, title: "Understanding Energy Field Patterns", views: 3780 },
    { id: 203, title: "Interpreting Chakra Analysis Results", views: 3450 },
    { id: 204, title: "Advanced Measurement Protocols", views: 2890 }]

  },
  {
    id: 3,
    title: "Software Features",
    icon: "ComputerDesktopIcon",
    articleCount: 28,
    articles: [
    { id: 301, title: "Navigating the Dashboard Interface", views: 3890 },
    { id: 302, title: "Creating Custom Reports", views: 3120 },
    { id: 303, title: "Data Export and Integration Options", views: 2760 },
    { id: 304, title: "Using Advanced Analysis Tools", views: 2340 }]

  },
  {
    id: 4,
    title: "Troubleshooting & Errors",
    icon: "ExclamationTriangleIcon",
    articleCount: 19,
    articles: [
    { id: 401, title: "Resolving Connection Issues", views: 5230 },
    { id: 402, title: "Calibration Error Solutions", views: 4560 },
    { id: 403, title: "Software Crash Recovery", views: 3890 },
    { id: 404, title: "Hardware Diagnostic Procedures", views: 3120 }]

  }];


  const videoTutorials = [
  {
    id: 1,
    title: "Complete Device Setup and First Scan",
    duration: "12:45",
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_111274e99-1764800400151.png",
    thumbnailAlt: "Professional instructor demonstrating Bio-Well device setup on modern desk with computer monitor",
    category: "Getting Started",
    views: 8920
  },
  {
    id: 2,
    title: "Understanding Your Energy Field Results",
    duration: "18:30",
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1ec53b7b6-1764800400028.png",
    thumbnailAlt: "Close-up of Bio-Well energy field visualization display showing colorful chakra patterns on screen",
    category: "Analysis",
    views: 7650
  },
  {
    id: 3,
    title: "Advanced Calibration Techniques",
    duration: "15:20",
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1f1b57cc1-1764700071486.png",
    thumbnailAlt: "Technical specialist performing device calibration with precision tools on laboratory workbench",
    category: "Maintenance",
    views: 6340
  },
  {
    id: 4,
    title: "Creating Professional Client Reports",
    duration: "22:15",
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_11dd292e2-1764667687408.png",
    thumbnailAlt: "Wellness practitioner reviewing detailed energy field report on tablet in modern clinic setting",
    category: "Software",
    views: 5890
  },
  {
    id: 5,
    title: "Troubleshooting Common Connection Issues",
    duration: "10:30",
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_18edc3c55-1766065006090.png",
    thumbnailAlt: "IT technician examining USB connections and cables on Bio-Well device with diagnostic tools",
    category: "Troubleshooting",
    views: 5120
  },
  {
    id: 6,
    title: "Optimizing Measurement Accuracy",
    duration: "16:45",
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_120f87a73-1766065006199.png",
    thumbnailAlt: "Researcher demonstrating proper finger placement technique on Bio-Well sensor plate in laboratory",
    category: "Best Practices",
    views: 4780
  }];


  const maintenanceGuides = [
  {
    id: 1,
    title: "Daily Cleaning Procedures",
    description: "Proper cleaning and sanitization of sensor plates and device surfaces",
    frequency: "Daily",
    icon: "SparklesIcon"
  },
  {
    id: 2,
    title: "Weekly System Checks",
    description: "Routine verification of device functionality and software updates",
    frequency: "Weekly",
    icon: "ClipboardDocumentCheckIcon"
  },
  {
    id: 3,
    title: "Monthly Calibration",
    description: "Comprehensive calibration procedures for optimal accuracy",
    frequency: "Monthly",
    icon: "AdjustmentsHorizontalIcon"
  },
  {
    id: 4,
    title: "Quarterly Deep Maintenance",
    description: "Thorough inspection and preventive maintenance protocols",
    frequency: "Quarterly",
    icon: "WrenchScrewdriverIcon"
  },
  {
    id: 5,
    title: "Annual Professional Service",
    description: "Factory-certified technician inspection and certification",
    frequency: "Annually",
    icon: "ShieldCheckIcon"
  },
  {
    id: 6,
    title: "Storage Best Practices",
    description: "Proper storage conditions and long-term care guidelines",
    frequency: "As Needed",
    icon: "ArchiveBoxIcon"
  }];


  const communityQuestions = [
  {
    id: 1,
    question: "What\'s the best way to explain energy field readings to new clients?",
    askedBy: "Dr. Sarah Mitchell",
    askedByImage: "https://img.rocket.new/generatedImages/rocket_gen_img_16309cf16-1763295285121.png",
    askedByImageAlt: "Professional woman with brown hair in white medical coat smiling at camera in clinic",
    answers: 12,
    views: 2340,
    votes: 28,
    tags: ["Client Communication", "Best Practices", "Education"],
    hasAcceptedAnswer: true
  },
  {
    id: 2,
    question: "How do environmental factors affect measurement accuracy?",
    askedBy: "James Rodriguez",
    askedByImage: "https://img.rocket.new/generatedImages/rocket_gen_img_111de63eb-1763293804928.png",
    askedByImageAlt: "Hispanic man with short black hair in navy blue shirt in modern office setting",
    answers: 8,
    views: 1890,
    votes: 22,
    tags: ["Measurement", "Technical", "Environment"],
    hasAcceptedAnswer: true
  },
  {
    id: 3,
    question: "Recommended protocols for stress assessment sessions?",
    askedBy: "Emily Chen",
    askedByImage: "https://img.rocket.new/generatedImages/rocket_gen_img_11f631977-1763295619675.png",
    askedByImageAlt: "Asian woman with long black hair in professional attire in wellness center",
    answers: 15,
    views: 3120,
    votes: 35,
    tags: ["Protocols", "Stress Assessment", "Clinical"],
    hasAcceptedAnswer: false
  },
  {
    id: 4,
    question: "Integration with existing practice management software?",
    askedBy: "Michael Thompson",
    askedByImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1dab17854-1765511199550.png",
    askedByImageAlt: "Professional man with glasses in gray suit jacket in modern office environment",
    answers: 6,
    views: 1560,
    votes: 18,
    tags: ["Software", "Integration", "Practice Management"],
    hasAcceptedAnswer: true
  }];


  const contactOptions = [
  {
    id: 1,
    title: "Email Support",
    description: "Get detailed responses within 24 hours",
    icon: "EnvelopeIcon",
    action: "Send Email",
    color: "bg-accent"
  },
  {
    id: 2,
    title: "Phone Support",
    description: "Speak directly with our technical team",
    icon: "PhoneIcon",
    action: "Call Now",
    color: "bg-quantum-purple"
  },
  {
    id: 3,
    title: "Remote Assistance",
    description: "Screen sharing for complex issues",
    icon: "ComputerDesktopIcon",
    action: "Request Session",
    color: "bg-energy-teal"
  },
  {
    id: 4,
    title: "Schedule Callback",
    description: "Choose a convenient time for us to call",
    icon: "CalendarIcon",
    action: "Schedule Now",
    color: "bg-cta-red"
  }];


  const handleSearchClick = () => {
    if (!isHydrated) return;
    console.log('Search knowledge base clicked');
  };

  const handleCardClick = (link: string) => {
    if (!isHydrated) return;
    console.log('Quick access card clicked:', link);
  };

  const handleArticleClick = (articleId: number) => {
    if (!isHydrated) return;
    console.log('Article clicked:', articleId);
  };

  const handleVideoClick = (videoId: number) => {
    if (!isHydrated) return;
    console.log('Video clicked:', videoId);
  };

  const handleStartChat = () => {
    if (!isHydrated) return;
    setIsZoomModalOpen(true);
  };

  const handleSubmitTicket = (data: TicketFormData) => {
    if (!isHydrated) return;
    console.log('Ticket submitted:', data);
    alert('Support ticket submitted successfully! Our team will respond within 24 hours.');
  };

  const handleGuideClick = (guideId: number) => {
    if (!isHydrated) return;
    console.log('Maintenance guide clicked:', guideId);
  };

  const handleQuestionClick = (questionId: number) => {
    if (!isHydrated) return;
    console.log('Community question clicked:', questionId);
  };

  const handleAskQuestion = () => {
    if (!isHydrated) return;
    console.log('Ask question clicked');
  };

  const handleOptionClick = (action: string) => {
    if (!isHydrated) return;
    console.log('Contact option clicked:', action);
  };

  if (!isHydrated) {
    return (
      <div data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:358:6" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="358" data-component-end-line="364" data-component-file="SupportCenterInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
        <div data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:359:8" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="359" data-component-end-line="359" data-component-file="SupportCenterInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-96%20bg-gradient-to-br%20from-primary%20via-secondary%20to-authority-blue%20animate-pulse%22%7D" className="h-96 bg-gradient-to-br from-primary via-secondary to-authority-blue animate-pulse"></div>
        <div data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:360:8" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="360" data-component-end-line="363" data-component-file="SupportCenterInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20px-4%20lg%3Apx-8%20py-16%20space-y-8%22%7D" className="max-w-7xl mx-auto px-4 lg:px-8 py-16 space-y-8">
          <div data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:361:10" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="361" data-component-end-line="361" data-component-file="SupportCenterInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-64%20bg-card%20rounded-xl%20animate-pulse%22%7D" className="h-64 bg-card rounded-xl animate-pulse"></div>
          <div data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:362:10" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="362" data-component-end-line="362" data-component-file="SupportCenterInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-64%20bg-card%20rounded-xl%20animate-pulse%22%7D" className="h-64 bg-card rounded-xl animate-pulse"></div>
        </div>
      </div>);

  }

  return (
    <div data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:369:4" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="369" data-component-end-line="412" data-component-file="SupportCenterInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
      <HeroSection data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:370:6" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="370" data-component-end-line="370" data-component-file="SupportCenterInteractive.tsx" data-component-name="HeroSection" data-component-content="%7B%22elementName%22%3A%22HeroSection%22%7D" onSearchClick={handleSearchClick} />
      <QuickAccessCards data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:371:6" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="371" data-component-end-line="371" data-component-file="SupportCenterInteractive.tsx" data-component-name="QuickAccessCards" data-component-content="%7B%22elementName%22%3A%22QuickAccessCards%22%7D" cards={quickAccessCards} onCardClick={handleCardClick} />
      <KnowledgeBaseSection data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:372:6" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="372" data-component-end-line="372" data-component-file="SupportCenterInteractive.tsx" data-component-name="KnowledgeBaseSection" data-component-content="%7B%22elementName%22%3A%22KnowledgeBaseSection%22%7D" categories={knowledgeBaseCategories} onArticleClick={handleArticleClick} />
      <VideoTutorialsSection data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:373:6" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="373" data-component-end-line="373" data-component-file="SupportCenterInteractive.tsx" data-component-name="VideoTutorialsSection" data-component-content="%7B%22elementName%22%3A%22VideoTutorialsSection%22%7D" tutorials={videoTutorials} onVideoClick={handleVideoClick} />
      <LiveChatSection data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:374:6" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="374" data-component-end-line="374" data-component-file="SupportCenterInteractive.tsx" data-component-name="LiveChatSection" data-component-content="%7B%22elementName%22%3A%22LiveChatSection%22%7D" onStartChat={handleStartChat} />
      <TicketSystemSection data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:375:6" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="375" data-component-end-line="375" data-component-file="SupportCenterInteractive.tsx" data-component-name="TicketSystemSection" data-component-content="%7B%22elementName%22%3A%22TicketSystemSection%22%7D" onSubmitTicket={handleSubmitTicket} />
      <MaintenanceSection data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:376:6" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="376" data-component-end-line="376" data-component-file="SupportCenterInteractive.tsx" data-component-name="MaintenanceSection" data-component-content="%7B%22elementName%22%3A%22MaintenanceSection%22%7D" guides={maintenanceGuides} onGuideClick={handleGuideClick} />
      <CommunityQASection data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:377:6" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="377" data-component-end-line="380" data-component-file="SupportCenterInteractive.tsx" data-component-name="CommunityQASection" data-component-content="%7B%22elementName%22%3A%22CommunityQASection%22%7D"
        questions={communityQuestions}
        onQuestionClick={handleQuestionClick}
        onAskQuestion={handleAskQuestion} />

      <ContactOptionsSection data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:382:6" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="382" data-component-end-line="382" data-component-file="SupportCenterInteractive.tsx" data-component-name="ContactOptionsSection" data-component-content="%7B%22elementName%22%3A%22ContactOptionsSection%22%7D" options={contactOptions} onOptionClick={handleOptionClick} />

      {/* Zoom Modal */}
      {isZoomModalOpen && (
        <div data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:386:8" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="386" data-component-end-line="410" data-component-file="SupportCenterInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22fixed%20inset-0%20bg-black%2F70%20backdrop-blur-sm%20z-50%20flex%20items-center%20justify-center%20p-4%22%7D" 
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setIsZoomModalOpen(false)}
        >
          <div data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:390:10" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="390" data-component-end-line="409" data-component-file="SupportCenterInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20w-full%20max-w-5xl%20bg-white%20rounded-xl%20shadow-2xl%22%7D" 
            className="relative w-full max-w-5xl bg-white rounded-xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:394:12" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="394" data-component-end-line="399" data-component-file="SupportCenterInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22absolute%20-top-4%20-right-4%20w-10%20h-10%20bg-white%20rounded-full%20shadow-lg%20flex%20items-center%20justify-center%20hover%3Abg-gray-100%20transition-colors%20z-10%22%7D"
              onClick={() => setIsZoomModalOpen(false)}
              className="absolute -top-4 -right-4 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-100 transition-colors z-10"
            >
              <span data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:398:14" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="398" data-component-end-line="398" data-component-file="SupportCenterInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-2xl%20text-gray-600%22%2C%22textContent%22%3A%22%C3%97%22%7D" className="text-2xl text-gray-600">&times;</span>
            </button>
            
            <div data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:401:12" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="401" data-component-end-line="408" data-component-file="SupportCenterInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20pt-%5B56.25%25%5D%22%7D" className="relative pt-[56.25%]">
              <iframe data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:402:14" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="402" data-component-end-line="407" data-component-file="SupportCenterInteractive.tsx" data-component-name="iframe" data-component-content="%7B%22elementName%22%3A%22iframe%22%2C%22src%22%3A%22https%3A%2F%2Fus06web.zoom.us%2Fj%2F4867523406%3Fpwd%3Dm5Xh0v9KDrMukunWMRsVu48PyAN7lH.1%23success%22%2C%22className%22%3A%22absolute%20top-0%20left-0%20w-full%20h-full%20rounded-xl%22%7D"
                src="https://us06web.zoom.us/j/4867523406?pwd=m5Xh0v9KDrMukunWMRsVu48PyAN7lH.1#success"
                className="absolute top-0 left-0 w-full h-full rounded-xl"
                allow="camera; microphone; fullscreen"
                title="Zoom Live Chat Meeting"
              />
            </div>
          </div>
        </div>
      )}
    </div>);

}